import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { format, differenceInSeconds, parseISO, startOfDay, endOfDay } from 'date-fns';
import { Card, Table, Pagination, Badge } from '@/components';
import { useSessions } from '@/hooks/useProApi';
import { formatVehicle, formatVin, formatDuration, utcToLocal, type SessionSummary } from '@/types';
import { analytics } from '@/utils/analytics';

// Get session status hint based on duration heuristic
function getSessionStatus(session: SessionSummary): 'brief' | 'incomplete' | 'full' {
  if (!session.updatedAt) return 'incomplete';
  const durationSeconds = differenceInSeconds(utcToLocal(session.updatedAt), utcToLocal(session.createdAt));
  if (durationSeconds < 60) return 'brief'; // Changed from 30 to 60 seconds
  return 'full';
}

// Get duration in seconds
function getDurationSeconds(session: SessionSummary): number {
  if (!session.updatedAt) return 0;
  return differenceInSeconds(utcToLocal(session.updatedAt), utcToLocal(session.createdAt));
}

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

type DurationFilter = 'all' | 'under1m' | '1m-5m' | 'over5m' | 'over1m';
type StatusFilter = 'all' | 'brief' | 'full';

// Default range: the last 12 months, as LOCAL calendar dates. toISOString() gave the UTC date,
// which is tomorrow in the evening west of UTC and still yesterday in the morning east of it -
// and "to: yesterday" hid that morning's sessions from a user in, say, Sydney.
function getDefaultStartDate(): string {
  const date = new Date();
  date.setFullYear(date.getFullYear() - 1);
  return format(date, 'yyyy-MM-dd');
}

function getDefaultEndDate(): string {
  return format(new Date(), 'yyyy-MM-dd');
}

export function Sessions() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Read initial state from URL, with smart defaults
  const [page, setPage] = useState(() => parseInt(searchParams.get('page') || '1'));
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('search') || '');
  const [selectedVehicle, setSelectedVehicle] = useState(() => searchParams.get('vehicle') || '');
  // Default to last 30 days if no URL params
  const [startDate, setStartDate] = useState(() => searchParams.get('startDate') || getDefaultStartDate());
  const [endDate, setEndDate] = useState(() => searchParams.get('endDate') || getDefaultEndDate());
  // Default to showing sessions >= 1 minute
  const [durationFilter, setDurationFilter] = useState<DurationFilter>(() => (searchParams.get('duration') as DurationFilter) || 'over1m');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(() => (searchParams.get('status') as StatusFilter) || 'all');

  const pageSize = 20;
  const debouncedSearch = useDebounce(searchQuery, 300);

  // Fetch all sessions (we'll filter client-side for the full feature set)
  const { data, isLoading } = useSessions({
    page: 1,
    pageSize: 1000, // Get more sessions for client-side filtering
  });

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (page > 1) params.set('page', String(page));
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (selectedVehicle) params.set('vehicle', selectedVehicle);
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    if (durationFilter !== 'all') params.set('duration', durationFilter);
    if (statusFilter !== 'all') params.set('status', statusFilter);
    setSearchParams(params, { replace: true });
  }, [page, debouncedSearch, selectedVehicle, startDate, endDate, durationFilter, statusFilter, setSearchParams]);

  // Track filter changes for analytics
  useEffect(() => {
    if (debouncedSearch) analytics.filterApplied('search', debouncedSearch);
  }, [debouncedSearch]);

  useEffect(() => {
    if (selectedVehicle) analytics.filterApplied('vehicle', selectedVehicle);
  }, [selectedVehicle]);

  useEffect(() => {
    if (durationFilter !== 'all') analytics.filterApplied('duration', durationFilter);
  }, [durationFilter]);

  useEffect(() => {
    if (statusFilter !== 'all') analytics.filterApplied('status', statusFilter);
  }, [statusFilter]);

  // Get unique vehicles for dropdown
  const uniqueVehicles = useMemo(() => {
    if (!data?.sessions) return [];
    const vehicleMap = new Map<string, { label: string; vin: string }>();
    data.sessions.forEach(s => {
      const key = s.vin || `${s.vehicleYear}-${s.vehicleMake}-${s.vehicleModel}`;
      if (!vehicleMap.has(key)) {
        vehicleMap.set(key, {
          label: formatVehicle(s.vehicleYear, s.vehicleMake, s.vehicleModel),
          vin: s.vin || '',
        });
      }
    });
    return Array.from(vehicleMap.entries()).map(([key, value]) => ({ key, ...value }));
  }, [data?.sessions]);

  // Apply all filters
  const filteredSessions = useMemo(() => {
    if (!data?.sessions) return [];

    return data.sessions.filter(session => {
      // Search filter (vehicle name or VIN)
      if (debouncedSearch) {
        const query = debouncedSearch.toLowerCase();
        const vehicleName = formatVehicle(session.vehicleYear, session.vehicleMake, session.vehicleModel).toLowerCase();
        const vin = (session.vin || '').toLowerCase();
        if (!vehicleName.includes(query) && !vin.includes(query)) return false;
      }

      // Vehicle filter
      if (selectedVehicle) {
        const sessionKey = session.vin || `${session.vehicleYear}-${session.vehicleMake}-${session.vehicleModel}`;
        if (sessionKey !== selectedVehicle) return false;
      }

      // Date range filter
      if (startDate || endDate) {
        const sessionDate = utcToLocal(session.createdAt);
        if (startDate && sessionDate < startOfDay(parseISO(startDate))) return false;
        if (endDate && sessionDate > endOfDay(parseISO(endDate))) return false;
      }

      // Duration filter
      const durationSecs = getDurationSeconds(session);
      if (durationFilter === 'under1m' && durationSecs >= 60) return false;
      if (durationFilter === '1m-5m' && (durationSecs < 60 || durationSecs >= 300)) return false;
      if (durationFilter === 'over5m' && durationSecs < 300) return false;
      if (durationFilter === 'over1m' && durationSecs < 60) return false; // Default: >= 1 minute

      // Status filter
      const status = getSessionStatus(session);
      if (statusFilter === 'brief' && status !== 'brief' && status !== 'incomplete') return false;
      if (statusFilter === 'full' && status !== 'full') return false;

      return true;
    });
  }, [data?.sessions, debouncedSearch, selectedVehicle, startDate, endDate, durationFilter, statusFilter]);

  // Paginate filtered results
  const paginatedSessions = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredSessions.slice(start, start + pageSize);
  }, [filteredSessions, page, pageSize]);

  const totalPages = Math.ceil(filteredSessions.length / pageSize);

  // Reset filters to defaults (not empty — defaults are last 30 days, >= 1 minute)
  const resetFilters = useCallback(() => {
    setSearchQuery('');
    setSelectedVehicle('');
    setStartDate(getDefaultStartDate());
    setEndDate(getDefaultEndDate());
    setDurationFilter('over1m');
    setStatusFilter('all');
    setPage(1);
  }, []);

  const hasActiveFilters = debouncedSearch || selectedVehicle || startDate || endDate || durationFilter !== 'all' || statusFilter !== 'all';

  const columns = [
    {
      key: 'vehicle',
      header: 'Vehicle',
      render: (item: SessionSummary) => (
        <div>
          <p className="font-medium">{formatVehicle(item.vehicleYear, item.vehicleMake, item.vehicleModel)}</p>
          <p className="text-gray-400 text-xs">{formatVin(item.vin)}</p>
        </div>
      ),
    },
    {
      key: 'startTime',
      header: 'Start Time',
      render: (item: SessionSummary) => (
        <span className="text-sm">{format(utcToLocal(item.createdAt), 'MMM d, yyyy h:mm a')}</span>
      ),
    },
    {
      key: 'endTime',
      header: 'End Time',
      render: (item: SessionSummary) => (
        <span className="text-sm text-gray-400">
          {item.updatedAt ? format(utcToLocal(item.updatedAt), 'MMM d, yyyy h:mm a') : '-'}
        </span>
      ),
    },
    {
      key: 'duration',
      header: 'Duration',
      render: (item: SessionSummary) => {
        const status = getSessionStatus(item);
        return (
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-purple-400">
              {formatDuration(item.createdAt, item.updatedAt)}
            </span>
            {status === 'brief' && (
              <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-gray-500/20 text-gray-400 border border-gray-500/30" title="Sessions under 1 minute may have incomplete telemetry data">
                Brief
              </span>
            )}
            {status === 'incomplete' && (
              <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-amber-500/20 text-amber-400 border border-amber-500/30" title="Session was not properly ended - telemetry may be missing">
                Incomplete
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: 'dtcCount',
      header: 'DTCs',
      render: (item: SessionSummary) => (
        <Badge variant={item.dtcCount > 0 ? 'error' : 'success'}>
          {item.dtcCount} {item.dtcCount === 1 ? 'code' : 'codes'}
        </Badge>
      ),
    },
    {
      key: 'hasReport',
      header: 'Report',
      render: (item: SessionSummary) =>
        item.hasReport ? (
          <Badge variant="info">Saved</Badge>
        ) : (
          <span className="text-gray-500">-</span>
        ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Scan Sessions</h1>
        <p className="text-gray-400 mt-1">Browse your diagnostic scan history</p>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          type="text"
          placeholder="Search by vehicle name or VIN..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setPage(1);
          }}
          className="w-full pl-12 pr-10 py-3 bg-obdai-primary border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition"
        />
        {searchQuery && (
          <button
            onClick={() => {
              setSearchQuery('');
              setPage(1);
            }}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Filter Row */}
      <div className="flex flex-wrap gap-3 items-center">
        {/* Vehicle Dropdown */}
        <select
          value={selectedVehicle}
          onChange={(e) => {
            setSelectedVehicle(e.target.value);
            setPage(1);
          }}
          className="px-4 py-2 bg-obdai-primary border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500 cursor-pointer"
        >
          <option value="">All Vehicles</option>
          {uniqueVehicles.map(v => (
            <option key={v.key} value={v.key}>{v.label}</option>
          ))}
        </select>

        {/* Date Range */}
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
              setPage(1);
            }}
            className="px-3 py-2 bg-obdai-primary border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500 [color-scheme:dark]"
            placeholder="Start Date"
          />
          <span className="text-gray-500">to</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => {
              setEndDate(e.target.value);
              setPage(1);
            }}
            className="px-3 py-2 bg-obdai-primary border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500 [color-scheme:dark]"
            placeholder="End Date"
          />
        </div>

        {/* Duration Buckets */}
        <div className="flex rounded-lg border border-gray-700 overflow-hidden">
          {[
            { value: 'all', label: 'All' },
            { value: 'over1m', label: '≥ 1m' },
            { value: 'under1m', label: '< 1m' },
            { value: '1m-5m', label: '1m – 5m' },
            { value: 'over5m', label: '> 5m' },
          ].map((option) => (
            <button
              key={option.value}
              onClick={() => {
                setDurationFilter(option.value as DurationFilter);
                setPage(1);
              }}
              className={`px-3 py-2 text-sm transition ${
                durationFilter === option.value
                  ? 'bg-purple-500/30 text-purple-300'
                  : 'bg-obdai-primary text-gray-400 hover:text-white hover:bg-white/5'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>

        {/* Status Filter */}
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value as StatusFilter);
            setPage(1);
          }}
          className="px-4 py-2 bg-obdai-primary border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500 cursor-pointer"
        >
          <option value="all">All Sessions</option>
          <option value="brief">Brief Sessions</option>
          <option value="full">Full Sessions</option>
        </select>

        {/* Reset Filters */}
        {hasActiveFilters && (
          <button
            onClick={resetFilters}
            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-400 hover:text-white transition"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Reset Filters
          </button>
        )}
      </div>

      {/* Result Count */}
      {data && (
        <p className="text-sm text-gray-400">
          Showing {filteredSessions.length > 0 ? (page - 1) * pageSize + 1 : 0} – {Math.min(page * pageSize, filteredSessions.length)} of {filteredSessions.length} sessions
          {hasActiveFilters && data.totalCount !== filteredSessions.length && (
            <span className="text-gray-500"> (filtered from {data.totalCount} total)</span>
          )}
        </p>
      )}

      {/* Sessions Table */}
      <Card>
        <Table
          columns={columns}
          data={paginatedSessions}
          keyExtractor={(item) => item.sessionKey}
          onRowClick={(item) => navigate(`/sessions/${item.sessionKey}`)}
          isLoading={isLoading}
          emptyMessage={
            hasActiveFilters
              ? 'No sessions match your filters'
              : 'No scan sessions yet. Start scanning with the OBDAI app!'
          }
        />

        {totalPages > 1 && (
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        )}
      </Card>
    </div>
  );
}
